import { Box, TableCell, TableRow, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

import { parseMoney } from "../lib/money";

export interface Column<T> {
  key: keyof T & string;
  label: string;
  width: number;
  align?: "left" | "right";
  /** Sposób oczyszczenia wartości wklejonej albo wpisanej. */
  parse?: (raw: string) => string;
  /** Zwraca komunikat, gdy wartość jest niepoprawna. */
  validate?: (value: string, row: T) => string | undefined;
}

export const parseText = (raw: string) => raw.trim();
export const parseNumber = (raw: string) => parseMoney(raw);

export interface Focus {
  row: number;
  col: number;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  focus: Focus | null;
  readOnly?: boolean;
  onFocus: (f: Focus) => void;
  onChange: (row: number, key: string, value: string) => void;
  /** Wklejenie bloku: tablica wierszy, każdy tablicą wartości kolumn. */
  onPasteBlock: (start: Focus, block: string[][]) => void;
  /**
   * Wyróżnienie wiersza, np. stanem akceptacji.
   *
   * Siatka nie wie, co znaczy „odrzucona" — zna tylko wartości kolumn.
   * Decyzję o tym, który wiersz wygląda inaczej, podejmuje ekran, który
   * ją osadza; inaczej reguły dziedzinowe wsiąkłyby w komponent do edycji.
   */
  rowSx?: (row: T) => object;
}

/**
 * Siatka do edycji klawiaturą, z wklejaniem wielu komórek naraz.
 *
 * Napisana ręcznie, bo w MUI X wklejanie bloku i dane drzewiaste są w wersji
 * płatnej. Zakres jest tu na tyle wąski — wiersze i kolumny znane z góry,
 * brak sortowania i grupowania — że własna implementacja kosztuje mniej
 * niż licencja i mniej niż obchodzenie cudzych ograniczeń.
 *
 * Wklejanie jest wymaganiem, nie udogodnieniem: pierwszy kosztorys zawsze
 * przychodzi jako arkusz, a przepisywanie stu pozycji ręcznie przesądza
 * o tym, że narzędzie nie zostanie użyte.
 */
export function Sheet<T extends Record<string, unknown>>({
  columns, rows, focus, readOnly, onFocus, onChange, onPasteBlock, rowSx,
}: Props<T>) {
  const kontener = useRef<HTMLTableSectionElement>(null);

  const przesun = useCallback(
    (dr: number, dc: number) => {
      if (!focus) return;
      const row = Math.max(0, Math.min(rows.length - 1, focus.row + dr));
      const col = Math.max(0, Math.min(columns.length - 1, focus.col + dc));
      onFocus({ row, col });
    },
    [focus, rows.length, columns.length, onFocus],
  );

  useEffect(() => {
    if (!focus || !kontener.current) return;
    const pole = kontener.current.querySelector<HTMLInputElement>(
      `[data-cell="${focus.row}-${focus.col}"]`,
    );
    if (pole && document.activeElement !== pole) pole.focus();
  }, [focus]);

  function naKlawisz(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "Enter") { e.preventDefault(); przesun(1, 0); }
    else if (e.key === "ArrowUp") { e.preventDefault(); przesun(-1, 0); }
    else if (e.key === "Tab") { e.preventDefault(); przesun(0, e.shiftKey ? -1 : 1); }
  }

  /** Komórka, od której zacząć wklejanie.
   *
   *  Zwykle wystarczy stan, ale gdy wklejenie nadejdzie w tej samej klatce
   *  co ustawienie ogniska, stan jest jeszcze pusty. Wtedy pytamy DOM —
   *  aktywny element zna swoje współrzędne. */
  function pozycjaStartowa(): Focus | null {
    if (focus) return focus;
    const aktywny = document.activeElement as HTMLElement | null;
    const wspolrzedne = aktywny?.dataset?.cell?.split("-");
    if (!wspolrzedne || wspolrzedne.length !== 2) return null;
    return { row: Number(wspolrzedne[0]), col: Number(wspolrzedne[1]) };
  }

  function naWklejenie(e: React.ClipboardEvent) {
    if (readOnly) return;
    const start = pozycjaStartowa();
    if (!start) return;
    const tekst = e.clipboardData.getData("text/plain");
    if (!tekst.includes("\t") && !tekst.includes("\n")) return; // pojedyncza komórka
    e.preventDefault();
    const blok = tekst
      .replace(/\r/g, "")
      .split("\n")
      .filter((linia, i, all) => linia !== "" || i < all.length - 1)
      .map((linia) => linia.split("\t"));
    onPasteBlock(start, blok);
  }

  return (
    <tbody ref={kontener} onPaste={naWklejenie}>
      {rows.map((row, r) => (
        <TableRow key={r} hover sx={rowSx?.(row)}>
          <TableCell sx={{ color: "text.disabled", width: 36, textAlign: "right" }}>
            {r + 1}
          </TableCell>
          {columns.map((col, c) => {
            const wartosc = String(row[col.key] ?? "");
            const problem = col.validate?.(wartosc, row);
            const pole = (
              <Box
                component="input"
                data-cell={`${r}-${c}`}
                value={wartosc}
                readOnly={readOnly}
                onFocus={() => onFocus({ row: r, col: c })}
                onKeyDown={naKlawisz}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(r, col.key, e.target.value)
                }
                onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                  col.parse && onChange(r, col.key, col.parse(e.target.value))
                }
                sx={{
                  width: "100%", border: "none", outline: "none", font: "inherit",
                  bgcolor: "transparent", padding: "2px 0",
                  textAlign: col.align ?? "left",
                  color: problem ? "error.main" : "inherit",
                  "&:focus": { bgcolor: "action.selected" },
                }}
              />
            );
            return (
              <TableCell key={col.key} sx={{ width: col.width }}>
                {problem ? <Tooltip title={problem}>{pole}</Tooltip> : pole}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </tbody>
  );
}
